/* ============================================================
   MakerWorldPrefillCard — MW-3 (TASK-40064910): the optional
   «Ссылка на модель MakerWorld» field in the quote form
   (docs/makerworld-link-quote-spec.md §5). Paste → debounce 700ms
   → POST /api/v1/quotes/prefill-from-link (MW-2, 1ca5f3cf):
   found → preview + confidence chips + editable grams/minutes
   (they flow into QuoteRequest.slicerGrams/slicerMinutes);
   found:false / network failure → fallback notice, the form keeps
   working exactly as today (spec §5.4). The link never replaces
   the file upload — the final quote still requires an analyzed
   model file. Light theme, Montserrat, iOS-18 minimal per
   DESIGN-SYSTEM.md.
   ============================================================ */

import { useEffect, useRef } from 'react';
import { api } from '../api/client';
import type { Material, MakerWorldPrefillDto, MakerWorldPrefillResultDto } from '../types';
import { CueChip } from './CueChip';
import {
  CHIP_LABELS,
  fieldsForProfile,
  prefillOutcome,
  resolveMaterialId,
  type MakerWorldPrefillView,
  type PrefillChipKind,
} from './makerWorldPrefillView';

/** Grams/minutes overrides that ride the existing quote request fields. */
export interface SlicerOverrideValues {
  grams: number | null;
  minutes: number | null;
}

export interface MakerWorldLinkState {
  status: 'idle' | 'loading' | 'found' | 'fallback';
  view: MakerWorldPrefillView | null;
  /** Raw MW-2 payload — needed to recompute fields on profile switch. */
  dto: MakerWorldPrefillDto | null;
  fallbackTitle: string | null;
  fallbackHint: string | null;
}

export const MAKERWORLD_LINK_INITIAL: MakerWorldLinkState = {
  status: 'idle',
  view: null,
  dto: null,
  fallbackTitle: null,
  fallbackHint: null,
};

/** Spec §5.2: paste → debounce 700ms → prefill request. */
export const PREFILL_DEBOUNCE_MS = 700;

interface MakerWorldPrefillCardProps {
  url: string;
  onUrlChange: (url: string) => void;
  state: MakerWorldLinkState;
  onStateChange: (state: MakerWorldLinkState) => void;
  /** Current grams/minutes overrides (owned by QuotePage — they ride the quote request). */
  values: SlicerOverrideValues;
  onValuesChange: (values: SlicerOverrideValues) => void;
  /** Catalog materials — a matching hint preselects one (spec §5.2). */
  materials: Material[];
  onMaterialSuggested: (materialId: string) => void;
}

export function MakerWorldPrefillCard({
  url,
  onUrlChange,
  state,
  onStateChange,
  values,
  onValuesChange,
  materials,
  onMaterialSuggested,
}: MakerWorldPrefillCardProps) {
  // Refs so the debounce effect (keyed on `url` only) always reads fresh props.
  const materialsRef = useRef(materials);
  materialsRef.current = materials;
  const suggestMaterialRef = useRef(onMaterialSuggested);
  suggestMaterialRef.current = onMaterialSuggested;
  // Last url a fetch was issued for — stale responses (user kept typing) are dropped.
  const fetchedFor = useRef<string | null>(null);

  useEffect(() => {
    const trimmed = url.trim();
    if (trimmed.length === 0) {
      fetchedFor.current = null;
      onStateChange(MAKERWORLD_LINK_INITIAL);
      onValuesChange({ grams: null, minutes: null });
      return;
    }
    if (fetchedFor.current === trimmed) return; // already resolved this exact url

    const timer = setTimeout(async () => {
      fetchedFor.current = trimmed;
      onStateChange({ ...MAKERWORLD_LINK_INITIAL, status: 'loading' });
      let result: MakerWorldPrefillResultDto | null = null;
      let networkError = false;
      try {
        result = await api.prefillFromLink(trimmed);
      } catch {
        networkError = true; // 429 / transport — same graceful §5.4 fallback
      }
      const outcome = prefillOutcome(result, networkError);
      if (outcome.status === 'found') {
        onStateChange({ status: 'found', view: outcome.view, dto: outcome.dto, fallbackTitle: null, fallbackHint: null });
        onValuesChange({ grams: outcome.view.grams.value, minutes: outcome.view.minutes.value });
        // §5.2 material preselect — only when the catalog actually has a match.
        const suggestedId = resolveMaterialId(outcome.view, materialsRef.current);
        if (suggestedId) suggestMaterialRef.current(suggestedId);
      } else {
        onStateChange({
          status: 'fallback',
          view: null,
          dto: null,
          fallbackTitle: outcome.fallback.title,
          fallbackHint: outcome.fallback.hint,
        });
        onValuesChange({ grams: null, minutes: null });
      }
    }, PREFILL_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // Keyed on url only: value/state changes must not re-trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  /** Profile switch: recompute grams/minutes from the MW-2 profile summary. */
  const selectProfile = (profileId: number) => {
    if (!state.view || !state.dto) return;
    const fields = fieldsForProfile(state.dto, profileId);
    onStateChange({
      ...state,
      view: { ...state.view, selectedProfileId: profileId, grams: fields.grams, minutes: fields.minutes },
    });
    onValuesChange({ grams: fields.grams.value, minutes: fields.minutes.value });
  };

  const parseNumber = (raw: string): number | null =>
    raw.trim().length === 0 || Number.isNaN(Number(raw)) ? null : Number(raw);

  return (
    <div className="card" style={{ padding: 'var(--space-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <div>
        <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-sm)' }}>
          ССЫЛКА НА МОДЕЛЬ MAKERWORLD · НЕОБЯЗАТЕЛЬНО
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center' }}>
          <input
            type="url"
            inputMode="url"
            placeholder="https://makerworld.com/ru/models/…"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            style={{ flex: 1 }}
            aria-label="Ссылка на модель MakerWorld"
          />
          {state.status === 'loading' && <div className="spinner" style={{ flexShrink: 0 }} />}
        </div>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-sm)' }}>
          Вставьте ссылку — мы подставим вес и время печати. Файл модели всё равно нужно загрузить.
        </p>
      </div>

      {/* §5.4 fallback — the form keeps living exactly as before */}
      {state.status === 'fallback' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            padding: 'var(--space-sm) var(--space-md)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-bg)',
          }}
        >
          <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)' }}>{state.fallbackTitle}</p>
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{state.fallbackHint}</p>
        </div>
      )}

      {state.status === 'found' && state.view && (
        <>
          {/* Preview: cover + title + creator (spec §5.2 превью) */}
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
            {state.view.preview.coverUrl ? (
              <img
                src={state.view.preview.coverUrl}
                alt=""
                style={{ width: 56, height: 56, borderRadius: 'var(--radius-md)', objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <ModelIcon />
            )}
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {state.view.preview.title}
              </p>
              {state.view.preview.creator && (
                <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  Автор: {state.view.preview.creator}
                  {state.view.preview.license ? ` · ${state.view.preview.license}` : ''}
                </p>
              )}
            </div>
          </div>

          {/* Multi-profile selector (spec §5.2: селектор профилей/плашек) */}
          {state.view.hasMultipleProfiles && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
              {state.view.profiles.map((p) => {
                const active = p.id === state.view!.selectedProfileId;
                return (
                  <button
                    key={p.id}
                    onClick={() => selectProfile(p.id)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 'var(--radius-pill)',
                      fontSize: 'var(--fs-caption)',
                      fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                      background: active ? 'var(--color-primary)' : 'var(--color-surface)',
                      color: active ? '#FFFFFF' : 'var(--color-text-secondary)',
                      border: active ? 'none' : '1px solid var(--color-separator)',
                      transition: 'all var(--transition-fast)',
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Prefilled editable fields + confidence chips (spec §5.2/§5.3) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <PrefillInput
              label="Вес, г"
              value={values.grams}
              onChange={(raw) => onValuesChange({ ...values, grams: parseNumber(raw) })}
              chip={state.view.grams.chip}
            />
            <PrefillInput
              label="Время, мин"
              value={values.minutes}
              onChange={(raw) => onValuesChange({ ...values, minutes: parseNumber(raw) })}
              chip={state.view.minutes.chip}
            />
          </div>

          {/* Material hint (read-only — the material itself is picked in the selector) */}
          {state.view.material && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
              {state.view.material.colorHex && (
                <span
                  aria-hidden
                  style={{ width: 14, height: 14, borderRadius: '50%', background: state.view.material.colorHex, border: '1px solid var(--color-separator)', flexShrink: 0 }}
                />
              )}
              <span style={{ fontSize: 'var(--fs-body)' }}>Материал по ссылке: {state.view.material.label}</span>
              <Chip chip={state.view.material.chip} />
            </div>
          )}

          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
            Габариты модели {CHIP_LABELS[state.view.dimensionsChip]} — берутся из загруженного файла.
          </p>
        </>
      )}
    </div>
  );
}

/* ── Editable prefill input with its confidence chip ─────── */

function Chip({ chip }: { chip: PrefillChipKind }) {
  return (
    <CueChip
      label={CHIP_LABELS[chip]}
      tone={chip === 'exact' ? 'success' : chip === 'approx' ? 'accent' : 'neutral'}
    />
  );
}

function PrefillInput({
  label,
  value,
  onChange,
  chip,
}: {
  label: string;
  value: number | null;
  onChange: (raw: string) => void;
  chip: PrefillChipKind;
}) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{label}</span>
        <Chip chip={chip} />
      </div>
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step="1"
        value={value ?? ''}
        placeholder={chip === 'manual' ? 'вручную' : ''}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      />
    </div>
  );
}

function ModelIcon() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="4" y="4" width="48" height="48" rx="12" fill="var(--color-bg)" />
      <path
        d="M28 12L44 21V39L28 48L12 39V21L28 12Z"
        stroke="var(--color-primary)"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M28 12V30M28 30L12 21M28 30L44 21" stroke="var(--color-primary)" strokeWidth="2" strokeLinejoin="round" opacity="0.6" />
    </svg>
  );
}
